import { Plus, Trash2 } from 'lucide-react';
import type { AdSet, BudgetConfig, MetaAdDraftInput } from '../../../types/metaAdDraft';
import type { MetaAdValidationIssue } from '../../../lib/metaAdValidation';
import { emptyAdSet } from '../emptyDraft';
import { reachOptimizationGoal } from '../previewNav';
import { useLinkedAdAccount } from '../useLinkedAdAccount';
import { useReachEstimate } from '../useReachEstimate';
import { Button } from '../../../components/ui/button';
import { BudgetFields } from '../components/BudgetFields';
import { ScheduleDateTimeField } from '../components/ScheduleDateTimeField';
import { PublishStatusToggle } from '../components/PublishStatusToggle';
import { AudienceSection } from './AudienceSection';
import { SavedAudienceControls } from '../components/SavedAudienceControls';
import { DEFAULT_END_TIME, DEFAULT_START_TIME } from '../../../lib/scheduleTime';

// Multi-ad-set: one campaign → N ad sets, each with its OWN targeting (the point:
// audience/location testing). What varies per ad set: targeting (always), ABO
// budget (when budgetMode==='ABO'), and the schedule (inherits the campaign
// default unless overridden). Each card reuses the full targeting UI (city search,
// radius clamp, reach estimate, validation) — now one instance per ad set.

// Display a wall-clock value as 'YYYY-MM-DD HH:mm' (or 'on publish' when unset).
const fmtDefault = (iso: string | null): string => {
  if (!iso) return 'on publish';
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  return m ? `${m[1]} ${m[2]}` : iso.slice(0, 10);
};

function ReachLine({
  accountId,
  adSet,
  optimizationGoal,
}: {
  accountId: string;
  adSet: AdSet;
  optimizationGoal: string;
}) {
  const isExtracted = adSet.targeting.sourceType === 'extracted';
  const enabled = accountId.trim().length > 0 && !isExtracted;
  const reach = useReachEstimate(accountId, adSet.targeting, reachOptimizationGoal(optimizationGoal), enabled);

  let text: string;
  if (isExtracted) text = 'Reach estimate unavailable for extracted targeting.';
  else if (!enabled) text = 'Pick a client to estimate reach.';
  else if (reach.isLoading || reach.isPolling) text = 'Estimating reach…';
  else if (reach.data?.estimable === false) text = 'Couldn’t estimate reach for this targeting.';
  else if (reach.isError || !reach.data || !reach.data.available || reach.data.estimateReady === false)
    text = 'Reach estimate unavailable.';
  else if (reach.data.lower != null && reach.data.upper != null)
    text = `Estimated reach: ${reach.data.lower.toLocaleString()}–${reach.data.upper.toLocaleString()} people`;
  else text = 'Reach range unavailable.';

  return <p className="text-xs text-slate-500">{text}</p>;
}

function AdSetCard({
  value,
  adSet,
  index,
  canRemove,
  onChange,
  onRemove,
  issues,
  accountName,
  fbAdAccountId,
}: {
  value: MetaAdDraftInput;
  adSet: AdSet;
  index: number;
  canRemove: boolean;
  onChange: (next: AdSet) => void;
  onRemove: () => void;
  issues: MetaAdValidationIssue[];
  accountName: string;
  fbAdAccountId: string | null;
}) {
  const { currency, timezoneName } = useLinkedAdAccount(value.accountId);
  const isAbo = value.campaign.budgetMode === 'ABO';
  const cardIssues = issues.filter((i) => i.adSetId === adSet.id);

  return (
    <div className="space-y-6 rounded-md border border-slate-200 p-5">
      {/* Per-ad-set publish status (Part 2 fan-out) — surfaces which ad sets
          succeeded / failed after a partial publish so the AM can retry the rest. */}
      {adSet.publishState && (
        <div
          className={[
            'flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium',
            adSet.publishState.status === 'complete'
              ? 'bg-green-50 text-green-700'
              : adSet.publishState.status === 'failed'
                ? 'bg-red-50 text-red-700'
                : 'bg-slate-50 text-slate-600',
          ].join(' ')}
        >
          {adSet.publishState.status === 'complete'
            ? 'Published to Meta ✓'
            : adSet.publishState.status === 'failed'
              ? `Publish failed${adSet.publishState.error ? ` — ${adSet.publishState.error}` : ''}`
              : `Publishing… (${adSet.publishState.status})`}
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <label htmlFor={`adset-name-${adSet.id}`} className="mb-1.5 block text-sm font-medium text-slate-700">
            Ad Set {index + 1} name
          </label>
          <input
            id={`adset-name-${adSet.id}`}
            type="text"
            value={adSet.name}
            onChange={(e) => onChange({ ...adSet, name: e.target.value })}
            placeholder="Defaults to campaign name"
            className="w-full max-w-md rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
          />
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ad set ${index + 1}`}
            className="mt-7 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" /> Remove
          </button>
        )}
      </div>

      {/* Per-ad-set Active/Off — publish THIS ad set live or paused-and-ready
          (e.g. load several audiences, run one, swap if it underperforms).
          Independent of the campaign's Publish state. */}
      <div className="space-y-1.5">
        <PublishStatusToggle
          label="Ad set state"
          value={adSet.status}
          onChange={(status) => onChange({ ...adSet, status })}
        />
      </div>

      {/* Targeting — the existing Audience UI, instantiated for THIS ad set. */}
      <AudienceSection
        targeting={adSet.targeting}
        accountId={value.accountId}
        onChange={(targeting) => onChange({ ...adSet, targeting })}
      />

      {/* Saved Audiences (Feature B) — save this ad set's targeting / apply a saved one. */}
      <SavedAudienceControls
        accountId={value.accountId}
        accountName={accountName}
        fbAdAccountId={fbAdAccountId}
        adSet={adSet}
        onChange={onChange}
      />

      {/* ABO budget — only when the campaign uses ad-set budgets. */}
      {isAbo && (
        <BudgetFields
          label="Ad set budget"
          value={adSet.budget}
          currency={currency}
          timezoneName={timezoneName}
          onChange={(budget: BudgetConfig) => onChange({ ...adSet, budget })}
        />
      )}

      {/* Schedule — inherits the campaign default unless overridden. */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            checked={adSet.datesOverridden}
            onChange={(e) => onChange({ ...adSet, datesOverridden: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-kyck-600 focus:ring-kyck-500"
          />
          Override campaign dates for this ad set
        </label>
        {adSet.datesOverridden ? (
          <div className="space-y-2">
            <div className="flex max-w-md flex-wrap gap-6">
              <div className="flex-1">
                <ScheduleDateTimeField
                  label="Start"
                  labelClassName="mb-1.5 block text-xs font-medium text-slate-600"
                  value={adSet.startDate}
                  defaultTime={DEFAULT_START_TIME}
                  onChange={(next) => onChange({ ...adSet, startDate: next })}
                />
              </div>
              <div className="flex-1">
                <ScheduleDateTimeField
                  label="End"
                  labelClassName="mb-1.5 block text-xs font-medium text-slate-600"
                  value={adSet.endDate}
                  defaultTime={DEFAULT_END_TIME}
                  onChange={(next) => onChange({ ...adSet, endDate: next })}
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              {value.campaign.scheduleUseAustralianEastern
                ? 'Times are Australian Eastern (AEST/AEDT).'
                : `Times are in the ad account's timezone${timezoneName ? ` (${timezoneName})` : ''}.`}
            </p>
          </div>
        ) : (
          <p className="text-xs text-slate-400">
            Inherits campaign dates ({fmtDefault(value.campaign.defaultStartDate)} –{' '}
            {fmtDefault(value.campaign.defaultEndDate)}).
          </p>
        )}
      </div>

      {/* Per-ad-set reach + validation. */}
      <ReachLine accountId={value.accountId} adSet={adSet} optimizationGoal={value.campaign.optimizationGoal} />
      {cardIssues.length > 0 && (
        <ul className="space-y-1 rounded-md bg-slate-50 px-3 py-2 text-xs">
          {cardIssues.map((i) => (
            <li key={i.code + i.field} className={i.severity === 'error' ? 'text-red-700' : 'text-amber-700'}>
              • {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdSetSection({
  value,
  onChange,
  issues,
  accountName,
  fbAdAccountId,
}: {
  value: MetaAdDraftInput;
  onChange: (patch: Partial<MetaAdDraftInput>) => void;
  issues: MetaAdValidationIssue[];
  accountName: string;
  fbAdAccountId: string | null;
}) {
  const setAdSet = (id: string, next: AdSet) =>
    onChange({ adSets: value.adSets.map((a) => (a.id === id ? next : a)) });
  const addAdSet = () => onChange({ adSets: [...value.adSets, emptyAdSet(crypto.randomUUID())] });
  const removeAdSet = (id: string) =>
    onChange({ adSets: value.adSets.filter((a) => a.id !== id) });

  return (
    <section id="adset" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Ad Sets</h2>
        <span className="text-xs text-slate-400">
          {value.adSets.length} ad set{value.adSets.length === 1 ? '' : 's'} · each tests its own audience
        </span>
      </div>

      <div className="space-y-6">
        {value.adSets.map((adSet, i) => (
          <AdSetCard
            key={adSet.id}
            value={value}
            adSet={adSet}
            index={i}
            canRemove={value.adSets.length > 1}
            onChange={(next) => setAdSet(adSet.id, next)}
            onRemove={() => removeAdSet(adSet.id)}
            issues={issues}
            accountName={accountName}
            fbAdAccountId={fbAdAccountId}
          />
        ))}
      </div>

      <Button type="button" variant="outline" onClick={addAdSet} className="gap-1">
        <Plus className="h-4 w-4" /> Add ad set
      </Button>
    </section>
  );
}
